import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Table,
} from "reactstrap";
import "./CommandeLine.css";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Select from "react-select";
import { supplierActions } from "../../sagas/supplierSlice";
import { productActions } from "../../sagas/productSlice";
import { categoryActions } from "../../sagas/categorySlice";
import { formatPrice, preventScroll } from "../../helpers/utility";

const CommandeLine = ({ formik, disabled }) => {
    const { values, setFieldValue } = formik;
    const [items, setItems] = useState([]);
    const [articleSearchByIndex, setArticleSearchByIndex] = useState({});
    const [activeSearchIndex, setActiveSearchIndex] = useState(null);
    const [showArticleModal, setShowArticleModal] = useState(false);
    const [articleLineIndex, setArticleLineIndex] = useState(null);
    const [articleForm, setArticleForm] = useState({
        libelle: "",
        codeBarre: "",
        categorieId: "",
        unite: "",
        prixAchatTTC: 0,
        prixVenteTTC1: 0,
        tauxTVAAchat: 0.2,
    });
    const [articleFormErrors, setArticleFormErrors] = useState({});
    const articleInputRefs = useRef({});

    const unites = useSelector((state) => state.products.unites);
    const { products, pageSize, loading } = useSelector((state) => state.products);
    const { list: categories } = useSelector((state) => state.categories);

    const dispatch = useDispatch();
    const createLineKey = () => `${Date.now()}-${Math.random()}`;
    const stripLineKey = ({ _lineKey, ...line }) => line;

    useEffect(() => {
        dispatch(productActions.findAllUnites());
        dispatch(categoryActions.list({ query: "" }));
    }, [dispatch]);

    useEffect(() => {
        setItems((currentItems) =>
            values.lignes.map((line, index) => ({
                ...line,
                _lineKey: line._lineKey || currentItems[index]?._lineKey || createLineKey(),
                unite: unites.find((u) => u.code === line.unite)?.libelle || line.unite || "",
            }))
        );
    }, [unites, values.lignes]);

    const roundMoney = (value, decimals = 2) => {
        return Number(
            Math.round((Number(value) + Number.EPSILON) * Math.pow(10, decimals)) /
                Math.pow(10, decimals)
        );
    };

    const recalculateTotals = (newItems) => {
        let totalHT = 0;
        let totalTVA = 0;
        let totalTTC = 0;

        newItems.forEach((item) => {
            totalHT += Number(item.montantHT) || 0;
            totalTVA += Number(item.montantTVA) || 0;
            totalTTC += Number(item.montantTTC) || 0;
        });

        setFieldValue("lignes", newItems.map(stripLineKey));
        setFieldValue("montantTotalHT", roundMoney(totalHT));
        setFieldValue("montantTotalTaxe", roundMoney(totalTVA));
        const discount = Math.min(Number(values.montantRemise) || 0, totalTTC);

        setFieldValue("montantRemise", roundMoney(discount));
        setFieldValue("montantTotalAvantRemise", roundMoney(totalTTC));
        setFieldValue(
            "montantTotalTTC",
            Math.max(0, roundMoney(totalTTC - discount))
        );
    };

    const handleInputChange = (index, event) => {
        const { name, value } = event.target;
        const newItems = [...items];

        newItems[index] = {
            ...newItems[index],
            [name]: value,
        };

        const quantity = Number(newItems[index].quantiteCommandee) || 0;
        const unitPriceTTC = Number(newItems[index].prixUnitaireTTC) || 0;
        const tauxTVA = Number(newItems[index].tauxTVA) || 0;

        const unitPriceHT = tauxTVA > 0
            ? roundMoney(unitPriceTTC / (1 + tauxTVA))
            : roundMoney(unitPriceTTC);

        const montantHT = roundMoney(quantity * unitPriceHT);
        const montantTTC = roundMoney(quantity * unitPriceTTC);
        const montantTVA = roundMoney(montantTTC - montantHT);

        newItems[index].prixUnitaireHT = unitPriceHT;
        newItems[index].montantHT = montantHT;
        newItems[index].montantTTC = montantTTC;
        newItems[index].montantTVA = montantTVA;

        setItems(newItems);
        recalculateTotals(newItems);
    };

    const addNewLine = () => {
        const newItem = {
            _lineKey: createLineKey(),
            articleId: null,
            articleNom: "",
            quantiteCommandee: 1,
            prixUnitaireHT: 0,
            prixUnitaireTTC: 0,
            unite: "",
            tauxTVA: 0.2,
            montantHT: 0,
            montantTTC: 0,
            montantTVA: 0,
        };

        const newItems = [...items, newItem];
        setItems(newItems);
        setFieldValue("lignes", newItems.map(stripLineKey));
        setActiveSearchIndex(newItems.length - 1);
    };

    const handleDeleteItem = (lineKey, event) => {
        event?.preventDefault();
        event?.stopPropagation();
        const deleteIndex = items.findIndex((item) => item._lineKey === lineKey);
        const newItems = items.filter((item) => item._lineKey !== lineKey);
        const nextArticleSearchByIndex = {};

        Object.entries(articleSearchByIndex).forEach(([key, value]) => {
            const oldIndex = Number(key);
            if (oldIndex < deleteIndex) {
                nextArticleSearchByIndex[oldIndex] = value;
            } else if (oldIndex > deleteIndex) {
                nextArticleSearchByIndex[oldIndex - 1] = value;
            }
        });

        setArticleSearchByIndex(nextArticleSearchByIndex);
        setActiveSearchIndex((current) => {
            if (current === null || deleteIndex < 0) return current;
            if (current === deleteIndex) return null;
            return current > deleteIndex ? current - 1 : current;
        });
        setItems(newItems);
        recalculateTotals(newItems);
    };

    const debouncedArticleSearch = useMemo(
        () =>
            debounce((query) => {
                if (!query || !query.trim()) return;

                dispatch(
                    productActions.findReception({
                        search: query,
                        page: 0,
                        size: pageSize || 10,
                    })
                );
            }, 300),
        [dispatch, pageSize]
    );

    useEffect(() => {
        return () => debouncedArticleSearch.cancel();
    }, [debouncedArticleSearch]);

    useEffect(() => {
        if (activeSearchIndex === null) return;

        const inputEl = articleInputRefs.current[activeSearchIndex];
        if (inputEl) {
            inputEl.focus();
            inputEl.select?.();
        }
    }, [activeSearchIndex, items.length]);

    const handleArticleSearchChange = (index, value) => {
        setArticleSearchByIndex((prev) => ({ ...prev, [index]: value }));
        setActiveSearchIndex(index);
        debouncedArticleSearch(value);
    };

    const formatArticleLabel = (article) => {
        const barcode = article?.codeBarre || article?.barcode || "";
        const libelle = article?.libelle || article?.articleNom || "";
        return barcode ? `${barcode}: ${libelle}` : libelle;
    };

    const clearArticleSelection = (index, event) => {
        event?.preventDefault();
        event?.stopPropagation();

        const newItems = [...items];
        const currentItem = newItems[index] || {};

        newItems[index] = {
            ...currentItem,
            articleId: null,
            articleNom: "",
            unite: "",
            prixUnitaireHT: 0,
            prixUnitaireTTC: 0,
            tauxTVA: 0.2,
            montantHT: 0,
            montantTTC: 0,
            montantTVA: 0,
        };

        setItems(newItems);
        recalculateTotals(newItems);
        setArticleSearchByIndex((prev) => ({
            ...prev,
            [index]: currentItem.articleNom || "",
        }));
        setActiveSearchIndex(index);
    };

    const getUniteLabel = (code) => unites.find((u) => u.code === code)?.libelle || code || "";

    const categoryOptions = useMemo(
        () =>
            (categories || []).map((category) => ({
                value: category.id,
                label: category.libelle,
            })),
        [categories]
    );

    const selectedCategoryOption = useMemo(
        () =>
            categoryOptions.find(
                (option) => String(option.value) === String(articleForm.categorieId)
            ) || null,
        [articleForm.categorieId, categoryOptions]
    );

    const applyProductToLine = (product, lineIndex, prixAchatHT) => {
        const tauxTVA = Number(product.tauxTVAAchat) || 0.2;
        const enteredQuantity = Number(items[lineIndex]?.quantiteCommandee);
        const quantiteCommandee = Number.isFinite(enteredQuantity) ? enteredQuantity : 1;
        const prixUnitaireHT = roundMoney(prixAchatHT);
        const prixUnitaireTTC = roundMoney(prixUnitaireHT * (1 + tauxTVA));
        const montantHT = roundMoney(quantiteCommandee * prixUnitaireHT);
        const montantTTC = roundMoney(quantiteCommandee * prixUnitaireTTC);

        const newItem = {
            _lineKey: items[lineIndex]?._lineKey || createLineKey(),
            articleId: product.id,
            articleNom: product.libelle,
            quantiteCommandee,
            prixUnitaireHT,
            prixUnitaireTTC,
            unite: getUniteLabel(product.unite),
            tauxTVA,
            montantHT,
            montantTTC,
            montantTVA: roundMoney(montantTTC - montantHT),
        };

        const newItems = [...items];
        newItems[lineIndex] = newItem;

        setItems(newItems);
        recalculateTotals(newItems);
        setArticleSearchByIndex((prev) => ({
            ...prev,
            [lineIndex]: formatArticleLabel(product),
        }));
        setActiveSearchIndex(null);
    };

    const handleProductSelect = (product, lineIndex) => {
        if (!values.fournisseurId) {
            Swal.fire({
                icon: "warning",
                title: t("purchase.noFournisseurTitle"),
                text: t("purchase.noFournisseurMessage"),
                confirmButtonText: t("common.ok", "OK"),
            });
            return;
        }

        const productExists = items.some(
            (item, index) => index !== lineIndex && item.articleId === product.id
        );
        if (productExists) {
            Swal.fire({
                icon: "warning",
                title: t("purchase.productExistsTitle"),
                text: t("purchase.productExistsMessage"),
                confirmButtonText: t("common.ok"),
            });
            return;
        }

        dispatch(
            supplierActions.findSupplierArticlePrice({
                fournisseurId: values.fournisseurId,
                productId: product.id,
                onSuccess: (data) => {
                    const prixAchatHT = Number(data) || 0;
                    applyProductToLine(product, lineIndex, prixAchatHT);
                },
            })
        );
    };

    const openArticleModal = (lineIndex) => {
        const searchedLabel = (articleSearchByIndex[lineIndex] || "").trim();
        setArticleLineIndex(lineIndex);
        setArticleForm({
            libelle: "",
            codeBarre: searchedLabel,
            categorieId: "",
            unite: unites[0]?.code || "",
            prixAchatTTC: Number(items[lineIndex]?.prixUnitaireTTC) || 0,
            prixVenteTTC1: Number(items[lineIndex]?.prixUnitaireTTC) || 0,
            tauxTVAAchat: Number(items[lineIndex]?.tauxTVA) || 0.2,
        });
        setArticleFormErrors({});
        setShowArticleModal(true);
    };

    const handleArticleFormChange = (event) => {
        const { name, value } = event.target;
        setArticleForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreateArticle = () => {
        const nextErrors = {};
        if (!articleForm.libelle.trim()) nextErrors.libelle = "Designation obligatoire";
        if (!articleForm.categorieId) nextErrors.categorieId = "Categorie obligatoire";
        if (!articleForm.unite) nextErrors.unite = "Unite obligatoire";
        if (Number(articleForm.prixAchatTTC) < 0) nextErrors.prixAchatTTC = "Prix invalide";
        if (Number(articleForm.prixVenteTTC1) < 0) nextErrors.prixVenteTTC1 = "Prix invalide";

        if (Object.keys(nextErrors).length > 0) {
            setArticleFormErrors(nextErrors);
            return;
        }

        const tauxTVAAchat = Number(articleForm.tauxTVAAchat) || 0.2;
        const prixAchatTTC = Number(articleForm.prixAchatTTC) || 0;
        const prixVenteTTC1 = Number(articleForm.prixVenteTTC1) || 0;
        const articleDTO = {
            libelle: articleForm.libelle.trim(),
            codeBarre: articleForm.codeBarre?.trim() || "",
            categorieId: articleForm.categorieId,
            typeArticle: "STOCKABLE",
            unite: articleForm.unite,
            prixAchatTTC,
            tauxTVAAchat,
            prixVenteTTC1,
            prixVenteTTC2: 0,
            prixVenteTTC3: 0,
            tauxTVAVente: tauxTVAAchat,
            stockMinimal: 0,
            stockPhysique: 0,
            stockDisponible: 0,
            prixVariable: false,
            enStock: true,
            actif: true,
        };

        const formData = new FormData();
        formData.append(
            "article",
            new Blob([JSON.stringify(articleDTO)], { type: "application/json" })
        );

        dispatch(
            productActions.addProduct({
                formData,
                onSuccess: (createdArticle) => {
                    const lineIndex = articleLineIndex;
                    setShowArticleModal(false);
                    setArticleFormErrors({});
                    dispatch(
                        productActions.findReception({
                            search: createdArticle?.libelle || articleDTO.libelle,
                            page: 0,
                            size: pageSize || 10,
                        })
                    );

                    if (lineIndex !== null && createdArticle?.id) {
                        const prixAchatHT = tauxTVAAchat > 0
                            ? roundMoney(prixAchatTTC / (1 + tauxTVAAchat))
                            : roundMoney(prixAchatTTC);
                        applyProductToLine(createdArticle, lineIndex, prixAchatHT);
                    }

                    Swal.fire({
                        icon: "success",
                        title: t("common.saved", "Saved"),
                        timer: 1200,
                        showConfirmButton: false,
                    });
                },
                onError: (error) => {
                    Swal.fire({
                        icon: "error",
                        title: t("common.error", "Error"),
                        text: error?.response?.data?.message || error?.message || "Article non cree",
                    });
                },
            })
        );
    };

    return (
        <Container fluid>
            <Table bordered className="commande-line-table">
                <thead>
                    <tr>
                        <th style={{ width: "420px", minWidth: "420px" }}>
                            {t("devis.article")}
                        </th>
                        <th>{t("devis.quantite")}</th>
                        <th>{t("devis.unite")}</th>
                        <th style={{ width: "200px" }}>P.U.</th>
                        <th>Total</th>
                        <th>{t("common.actions")}</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr>
                            <td colSpan="6" className="text-center py-4">
                                <div className="text-muted">{t("purchase.noItems")}</div>
                            </td>
                        </tr>
                    ) : (
                        items.map((item, index) => (
                            <tr key={item._lineKey || index}>
                                <td>
                                    <div style={{ position: "relative" }}>
                                        {item.articleId ? (
                                            <div className="commande-line-article-selected">
                                                <div
                                                    className="form-control commande-line-article-label"
                                                    title={articleSearchByIndex[index] || formatArticleLabel(item)}
                                                >
                                                    {articleSearchByIndex[index] || formatArticleLabel(item)}
                                                </div>
                                                {!disabled && (
                                                    <Button
                                                        type="button"
                                                        color="light"
                                                        className="commande-line-article-edit"
                                                        onClick={(event) => clearArticleSelection(index, event)}
                                                        title={t("common.edit", "Edit")}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                )}
                                            </div>
                                        ) : (
                                            <Input
                                                type="text"
                                                name="articleNom"
                                                value={articleSearchByIndex[index] ?? ""}
                                                onChange={(event) =>
                                                    handleArticleSearchChange(index, event.target.value)
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }
                                                }}
                                                placeholder={t("product.searchPlaceholder")}
                                                disabled={disabled}
                                                innerRef={(el) => {
                                                    if (el) articleInputRefs.current[index] = el;
                                                }}
                                            />
                                        )}
                                        {!item.articleId &&
                                            activeSearchIndex === index &&
                                            (articleSearchByIndex[index] ?? "").trim().length > 0 && (
                                                <div
                                                    className="bg-white border rounded shadow-sm"
                                                    style={{
                                                        position: "absolute",
                                                        top: "100%",
                                                        left: 0,
                                                        right: 0,
                                                        zIndex: 20,
                                                        maxHeight: "220px",
                                                        overflowY: "auto",
                                                    }}
                                                >
                                                    {loading ? (
                                                        <div className="p-2 text-muted small">
                                                            {t("common.loading", "Loading...")}
                                                        </div>
                                                    ) : !products || products.length === 0 ? (
                                                        <div className="p-2">
                                                            <div className="text-muted small mb-2">
                                                                {t("product.noProducts")}
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                color="primary"
                                                                size="sm"
                                                                className="w-100"
                                                                onMouseDown={(event) => event.preventDefault()}
                                                                onClick={() => openArticleModal(index)}
                                                            >
                                                                <FaPlus className="me-1" />
                                                                {t("product.new", "Nouvel article")}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {products.map((product) => (
                                                                <button
                                                                    key={product.id}
                                                                    type="button"
                                                                    className="w-100 text-start border-0 bg-white px-2 py-2"
                                                                    onClick={() =>
                                                                        handleProductSelect(product, index)
                                                                    }
                                                                >
                                                                    <div className="fw-semibold">
                                                                        {product.libelle}
                                                                    </div>
                                                                    <div className="small text-muted">
                                                                        {product.codeBarre} -{" "}
                                                                        {formatPrice(product.prixAchatTTC)}
                                                                    </div>
                                                                </button>
                                                            ))}
                                                            <div className="border-top p-2">
                                                                <Button
                                                                    type="button"
                                                                    color="light"
                                                                    size="sm"
                                                                    className="w-100"
                                                                    onMouseDown={(event) => event.preventDefault()}
                                                                    onClick={() => openArticleModal(index)}
                                                                >
                                                                    <FaPlus className="me-1" />
                                                                    {t("product.new", "Nouvel article")}
                                                                </Button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                </td>
                                <td>
                                    <Input
                                        type="number"
                                        name="quantiteCommandee"
                                        value={item.quantiteCommandee}
                                        onChange={(event) => handleInputChange(index, event)}
                                        disabled={disabled}
                                        className="no-spinner"
                                        onFocus={(e) => {
                                            e.target.select();
                                            e.target.addEventListener("wheel", preventScroll, {
                                                passive: false,
                                            });
                                        }}
                                        onBlur={(e) => {
                                            e.target.removeEventListener("wheel", preventScroll);
                                        }}
                                    />
                                </td>
                                <td>
                                    <Input type="text" name="unite" value={item.unite} disabled />
                                </td>
                                <td style={{ width: "100px" }}>
                                    <Input
                                        type="number"
                                        name="prixUnitaireTTC"
                                        value={item.prixUnitaireTTC}
                                        onChange={(event) => handleInputChange(index, event)}
                                        disabled={disabled}
                                        className="no-spinner"
                                        onFocus={(e) => {
                                            e.target.select();
                                            e.target.addEventListener("wheel", preventScroll, {
                                                passive: false,
                                            });
                                        }}
                                        onBlur={(e) => {
                                            e.target.removeEventListener("wheel", preventScroll);
                                        }}
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        name="montantTTC"
                                        value={formatPrice(item.montantTTC, false)}
                                        disabled
                                    />
                                </td>
                                <td>
                                    <Button
                                        color="danger"
                                        type="button"
                                        onClick={(event) => handleDeleteItem(item._lineKey, event)}
                                        size="sm"
                                        disabled={disabled}
                                    >
                                        <FaTrash />
                                    </Button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
            <Button
                type="button"
                color="primary"
                onClick={addNewLine}
                disabled={disabled}
                className="mb-3"
            >
                <FaPlus /> {t("facture.addLine")}
            </Button>
            <Modal isOpen={showArticleModal} toggle={() => setShowArticleModal(false)} size="lg">
                <ModalHeader toggle={() => setShowArticleModal(false)}>
                    {t("product.new", "Nouvel article")}
                </ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label for="articleLibelle">Designation *</Label>
                                <Input
                                    id="articleLibelle"
                                    name="libelle"
                                    value={articleForm.libelle}
                                    onChange={handleArticleFormChange}
                                    invalid={Boolean(articleFormErrors.libelle)}
                                />
                                {articleFormErrors.libelle && (
                                    <div className="text-danger small mt-1">
                                        {articleFormErrors.libelle}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={12}>
                            <FormGroup>
                                <Label for="articleCodeBarre">{t("product.barcode", "Code barre")}</Label>
                                <Input
                                    id="articleCodeBarre"
                                    name="codeBarre"
                                    value={articleForm.codeBarre}
                                    onChange={handleArticleFormChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={12}>
                            <FormGroup>
                                <Label for="articleCategorie">{t("product.category", "Categorie")} *</Label>
                                <Select
                                    id="articleCategorie"
                                    name="categorieId"
                                    className={articleFormErrors.categorieId ? "is-invalid" : ""}
                                    classNamePrefix="react-select"
                                    options={categoryOptions}
                                    value={selectedCategoryOption}
                                    onChange={(option) =>
                                        setArticleForm((prev) => ({
                                            ...prev,
                                            categorieId: option?.value || "",
                                        }))
                                    }
                                    onInputChange={(value, meta) => {
                                        if (meta.action === "input-change") {
                                            dispatch(categoryActions.list({ query: value }));
                                        }
                                        return value;
                                    }}
                                    placeholder={t("common.select", "Selectionner")}
                                    isSearchable
                                    isClearable
                                    maxMenuHeight={180}
                                />
                                {articleFormErrors.categorieId && (
                                    <div className="text-danger small mt-1">
                                        {articleFormErrors.categorieId}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label for="articleUnite">{t("devis.unite")} *</Label>
                                <Input
                                    type="select"
                                    id="articleUnite"
                                    name="unite"
                                    value={articleForm.unite}
                                    onChange={handleArticleFormChange}
                                    invalid={Boolean(articleFormErrors.unite)}
                                >
                                    <option value="">{t("common.select", "Selectionner")}</option>
                                    {unites.map((unite) => (
                                        <option key={unite.code} value={unite.code}>
                                            {unite.libelle} ({unite.code})
                                        </option>
                                    ))}
                                </Input>
                                {articleFormErrors.unite && (
                                    <div className="text-danger small mt-1">
                                        {articleFormErrors.unite}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={12}>
                            <FormGroup>
                                <Label for="articlePrixAchatTTC">Prix achat TTC</Label>
                                <Input
                                    type="number"
                                    id="articlePrixAchatTTC"
                                    name="prixAchatTTC"
                                    min="0"
                                    step="0.01"
                                    value={articleForm.prixAchatTTC}
                                    onChange={handleArticleFormChange}
                                    invalid={Boolean(articleFormErrors.prixAchatTTC)}
                                    onFocus={(event) => event.target.select()}
                                />
                                {articleFormErrors.prixAchatTTC && (
                                    <div className="text-danger small mt-1">
                                        {articleFormErrors.prixAchatTTC}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={12}>
                            <FormGroup>
                                <Label for="articlePrixVenteTTC1">Prix de vente 1</Label>
                                <Input
                                    type="number"
                                    id="articlePrixVenteTTC1"
                                    name="prixVenteTTC1"
                                    min="0"
                                    step="0.01"
                                    value={articleForm.prixVenteTTC1}
                                    onChange={handleArticleFormChange}
                                    invalid={Boolean(articleFormErrors.prixVenteTTC1)}
                                    onFocus={(event) => event.target.select()}
                                />
                                {articleFormErrors.prixVenteTTC1 && (
                                    <div className="text-danger small mt-1">
                                        {articleFormErrors.prixVenteTTC1}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Prix achat HT</Label>
                                <Input
                                    value={formatPrice(
                                        Number(articleForm.tauxTVAAchat) > 0
                                            ? Number(articleForm.prixAchatTTC || 0) /
                                                (1 + Number(articleForm.tauxTVAAchat || 0))
                                            : Number(articleForm.prixAchatTTC || 0),
                                        false
                                    )}
                                    disabled
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="primary" onClick={handleCreateArticle}>
                        {t("common.save")}
                    </Button>
                    <Button type="button" color="secondary" onClick={() => setShowArticleModal(false)}>
                        {t("common.cancel")}
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default CommandeLine;
